// src/features/messages/CreateGroupDialog.tsx — titled group creation: title +
// multi-select member picker (min 1, i.e. 2 including the creator).
import { useState } from 'react';
import styled from 'styled-components';
import { Dialog, Button, TextField, Avatar } from '@/ui';
import { Icon } from '@/icons';
import { useMessages, useSession } from '@/hooks';
import { ApiError } from '@/core/lib/apiClient';
import { PersonPicker } from './PersonPicker';
import type { PersonOption } from './PersonPicker';

const TITLE_MAX = 120;

const Stack = styled.div`display: flex; flex-direction: column; gap: 14px;`;
const TitleRow = styled.div`display: flex; flex-direction: column; gap: 4px;`;
const Counter = styled.span<{ $over: boolean }>`
  align-self: flex-end; font-family: ${({ theme }) => theme.font.mono};
  font-size: ${({ theme }) => theme.fontSize.xs};
  color: ${({ theme, $over }) => ($over ? theme.color.danger : theme.color.textMuted)};
`;
const ChipRow = styled.div`display: flex; flex-wrap: wrap; gap: 6px;`;
const Chip = styled.span`
  display: inline-flex; align-items: center; gap: 6px; padding: 4px 6px 4px 4px;
  border-radius: ${({ theme }) => theme.radius.md}; border: 1px solid ${({ theme }) => theme.color.glassBorder};
  background: ${({ theme }) => theme.color.glassBg};
  font-family: ${({ theme }) => theme.font.body}; font-size: ${({ theme }) => theme.fontSize.xs};
  color: ${({ theme }) => theme.color.textPrimary};
`;
const ChipRemove = styled.button`
  display: inline-grid; place-items: center; width: 16px; height: 16px; border: none;
  border-radius: 50%; background: transparent; color: ${({ theme }) => theme.color.textMuted};
  cursor: pointer;
  &:hover { background: ${({ theme }) => theme.color.hover}; color: ${({ theme }) => theme.color.textPrimary}; }
`;
const Hint = styled.p`
  margin: 0; font-family: ${({ theme }) => theme.font.body}; font-size: ${({ theme }) => theme.fontSize.xs};
  color: ${({ theme }) => theme.color.textMuted};
`;
const ErrorText = styled.p`
  margin: 0; font-family: ${({ theme }) => theme.font.body}; font-size: ${({ theme }) => theme.fontSize.sm};
  color: ${({ theme }) => theme.color.danger};
`;
const ButtonRow = styled.div`display: flex; gap: 10px; justify-content: flex-end;`;

export interface CreateGroupDialogProps {
  open: boolean;
  onClose: () => void;
  onCreated: (conversationId: string) => void;
}

export function CreateGroupDialog({ open, onClose, onCreated }: CreateGroupDialogProps) {
  const { createGroup } = useMessages();
  const { currentUser } = useSession();
  const [title, setTitle] = useState('');
  const [members, setMembers] = useState<PersonOption[]>([]);
  const [titleError, setTitleError] = useState<string | null>(null);
  const [memberError, setMemberError] = useState<string | null>(null);
  const [capError, setCapError] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);

  const trimmedTitle = title.trim();
  const overCap = title.length > TITLE_MAX;

  function addMember(p: PersonOption) {
    setMemberError(null);
    setCapError(null);
    setMembers((cur) => (cur.some((m) => m.handle === p.handle) ? cur : [...cur, p]));
  }

  function removeMember(handle: string) {
    setMembers((cur) => cur.filter((m) => m.handle !== handle));
  }

  async function handleSubmit() {
    setTitleError(null);
    setMemberError(null);
    setCapError(null);

    if (trimmedTitle === '' || trimmedTitle.length > TITLE_MAX) {
      setTitleError('Title is required, 1-120 characters.');
      return;
    }
    if (members.length === 0) {
      setMemberError('Add at least one person.');
      return;
    }

    setSubmitting(true);
    try {
      const convo = await createGroup(trimmedTitle, members.map((m) => m.handle));
      onCreated(convo.id);
    } catch (e) {
      if (e instanceof ApiError && e.code === 'validation' && typeof e.fields?.title === 'string') {
        setTitleError(e.fields.title);
      } else if (e instanceof ApiError && e.code === 'group_cap') {
        const cap = e.fields?.cap;
        setCapError(
          typeof cap === 'number'
            ? `Groups are limited to ${cap} people right now.`
            : 'Groups are limited to a maximum size right now.',
        );
      } else if (e instanceof ApiError && e.code === 'not_found' && typeof e.fields?.handle === 'string') {
        setMemberError(`We couldn't find ${e.fields.handle}.`);
      } else {
        setMemberError('Something went wrong. Please try again.');
      }
    } finally {
      setSubmitting(false);
    }
  }

  const exclude = [...members.map((m) => m.handle), ...(currentUser ? [currentUser.handle] : [])];

  return (
    <Dialog open={open} onClose={onClose} title="New group">
      <Stack>
        <TitleRow>
          <TextField
            label="Group title"
            placeholder="Name this group"
            value={title}
            onChange={(e) => {
              setTitle(e.target.value);
              setTitleError(null);
            }}
            error={titleError ?? undefined}
            disabled={submitting}
            maxLength={TITLE_MAX + 20}
          />
          <Counter $over={overCap}>
            {title.length}/{TITLE_MAX}
          </Counter>
        </TitleRow>

        {members.length > 0 && (
          <ChipRow>
            {members.map((m) => (
              <Chip key={m.handle}>
                <Avatar name={m.name} size={20} avatarKey={m.avatarKey} />
                {m.name}
                <ChipRemove
                  type="button"
                  onClick={() => removeMember(m.handle)}
                  aria-label={`Remove ${m.name}`}
                  disabled={submitting}
                >
                  <Icon name="close" size={12} />
                </ChipRemove>
              </Chip>
            ))}
          </ChipRow>
        )}

        <PersonPicker
          exclude={exclude}
          onPick={addMember}
          error={memberError}
          onQueryChange={() => setMemberError(null)}
          disabled={submitting}
        />

        <Hint>Groups have a size limit for now. Members may need to accept an invite first.</Hint>
        {capError && <ErrorText role="alert">{capError}</ErrorText>}

        <ButtonRow>
          <Button variant="ghost" onClick={onClose} disabled={submitting}>
            Cancel
          </Button>
          <Button variant="primary" onClick={() => void handleSubmit()} disabled={submitting}>
            {submitting ? 'Creating…' : 'Create group'}
          </Button>
        </ButtonRow>
      </Stack>
    </Dialog>
  );
}
